import styled from '@emotion/styled'
import { Button } from '@nextui-org/react-latest'
import { useState, useEffect, useRef } from 'react'
import * as React from 'react'

type Props = {
  title: string
  description: string
  illustration: React.ReactNode
  direction: 'row-reverse' | 'row'
  hideBtn?: boolean
  onBtnClick?: () => void
  animationBlockRatio?: string
  btnLabel: string
}

const About = ({
  title,
  description,
  illustration,
  direction,
  hideBtn = false,
  onBtnClick,
  animationBlockRatio = '16/10.55',
  btnLabel,
}: Props) => {
  const [isAnimationActive, setIsAnimationActive] = useState(false)
  const animationRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAnimationActive(true) // Start animation
          // observer.disconnect() // Stop observing after triggering once
        } else {
          setIsAnimationActive(false)
        }
      },
      {
        threshold: 0, // Adjust this value based on your needs
      }
    )

    if (animationRef.current) {
      observer.observe(animationRef.current)
    }

    return () => observer.disconnect() // Cleanup observer on unmount
  }, [])

  return (
    <Container ref={animationRef} direction={direction}>
      <Left>
        <Title>{title}</Title>
        <Description>{description}</Description>
        {!hideBtn && (
          <Button
            className="px-14 py-7 text-base font-regular max-w-max"
            radius="full"
            color="primary"
            onPress={() => {
              if (onBtnClick) {
                onBtnClick()
              }
            }}
          >
            {btnLabel}
          </Button>
        )}
      </Left>
      <Right animationBlockRatio={animationBlockRatio}>
        {isAnimationActive ? illustration : <></>}
      </Right>
    </Container>
  )
}

export default About

const Container = styled.div`
  max-width: 1450px;
  margin: 6rem auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 3rem;
  width: 100%;
  flex-direction: ${(props) => props.direction};
  @media (max-width: 1470px) {
    padding: 0 1rem;
  }
  @media (max-width: 769px) {
    flex-direction: column;
    align-items: start;
  }
`
const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  flex: 1;
`
const Title = styled.div`
  font-family: var(--font-roboto);
  font-size: 38px;
  font-weight: 600;
  line-height: 38px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #1a1e3d;
`
const Description = styled.div`
  font-family: var(--font-roboto);
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: #53566e;
  max-width: 625px;
`
const Right = styled.div`
  flex: 1;
  aspect-ratio: ${(props) => props.animationBlockRatio};
  /* aspect-ratio: 16.1/9.75; */
  @media (max-width: 769px) {
    flex-direction: column;
    align-items: start;
    width: 100%;
  }
`
